/* eslint-disable import/prefer-default-export */
import { find, includes, isEmpty, isNull, last, map, mapValues, merge, omit } from 'lodash';
import { casingFormat, formatName } from 'common/utils';

const constructDataForDuplicationReview = (prescriptions) => {
  const data = map(prescriptions, (prescription) => {
    const { instructions, prescriber } = prescription.request.medication;
    const { firstName, lastName, middleName, title, npi } = prescriber;
    return {
      ...omit(prescription, ['isActive', 'prescriptionUUID', 'originalRx', 'refillsRemaining']),
      npi,
      instructions,
      prescriber: `${title || ''} ${firstName} ${middleName || ''} ${lastName}`,
      refillsRemaining: !isNull(prescription.refillsRemaining)
        ? `${prescription.refillsRemaining}`
        : prescription.refillsRemaining,
      ...(prescription.drug ? { drug: casingFormat(prescription.drug) } : {}),
      ...(prescription.strength ? { strength: casingFormat(prescription.strength) } : {}),
      ...(prescription.prescribedDrugName
        ? { prescribedDrugName: casingFormat(prescription.prescribedDrugName) }
        : {}),
    };
  });

  return data;
};

/**
 * Resolves the review queue's NDC from the prescription_override payload. The override arrives
 * as productDetails (productId flow) or drugProfile (legacy), never both; each records an NDC, so
 * either branch can supply it.
 *
 * Drug name and strength are deliberately not resolved here. This queue reads them from the
 * prescription payload, which is what the server sorts and filters drugName on
 * (prescriptionEligibilityService's sortClause and drugName filter). Taking the displayed name
 * from the override while the server ordered and filtered on the payload made an overridden item
 * show one drug while being sorted and searched under another.
 */
const resolveOverrideDetails = (prescriptionOverride) => {
  const { productDetails, drugProfile } = prescriptionOverride || {};

  return {
    ndc: (productDetails || drugProfile)?.ndc,
  };
};

export const transformListPrescriptionsForReview = ({ listPrescriptionForReview }) => {
  return {
    count: listPrescriptionForReview.count,
    prescriptions: map(
      listPrescriptionForReview.result,
      ({
        orderId,
        tags: itemTags,
        patient,
        prescription,
        lineItem,
        fulfillingPharmacyId,
        source,
        isReshipment,
        isMarkedForFillLater,
        reviewType,
        fillType,
        isManualReview,
      }) => {
        const {
          rxNumber,
          drug: rawDrug,
          prescriptionUUID,
          strength: rawStrength,
          request,
          form,
          prescriptionOverride,
        } = prescription;
        const drug = rawDrug ? casingFormat(rawDrug) : rawDrug;
        const strength = rawStrength ? casingFormat(rawStrength) : rawStrength;
        const { patientDemographics, patientContact, patientIdentity } = patient;
        const { firstName, middleName, lastName, dob, mpi, gender, originalPartner } =
          patientDemographics;
        const { addresses, emails, phoneNumbers } = patientContact;
        const { address } = find(addresses, ({ tags }) => includes(tags, 'DEFAULT'));
        const { email } = find(emails, ({ tags }) => includes(tags, 'DEFAULT'));
        const { phoneNumber: mobilePhone } = find(phoneNumbers, ({ tags }) =>
          includes(tags, 'DEFAULT')
        );
        return {
          orderId,
          tags: itemTags,
          status: reviewType,
          rxNumber,
          drugName: drug,
          strength,
          ...resolveOverrideDetails(prescriptionOverride),
          fillType,
          requestedDate: lineItem?.requestedDate,
          pomPrescriptionId: prescriptionUUID,
          instructions: request?.medication?.instructions,
          needsByDate: lineItem?.needsByDate,
          shipByDate: lineItem?.shipByDate,
          shippingOption: lineItem?.shippingOption,
          inBatchShipping: lineItem?.inBatchShipping,
          isBusinessAddress: lineItem?.isBusinessAddress,
          form,
          patient: {
            dob,
            patientName: `${casingFormat(formatName(lastName))}, ${casingFormat(
              formatName(firstName)
            )}`,
            phoneNumber: find(patientContact.phoneNumbers, (phoneNumber) =>
              includes(phoneNumber.tags, 'DEFAULT')
            ).phoneNumber,
            patientId: mpi,
            qs1Id: patientIdentity?.qs1PatientId,
            middleName,
            firstName: casingFormat(formatName(firstName)),
            lastName: casingFormat(formatName(lastName)),
            address: { ...address, addressLine1: address.line1, addressLine2: address?.line2 },
            gender,
            mpi,
            email,
            mobilePhone,
            originalPartner,
          },
          prescriptionData: {
            drug,
            form,
            strength,
            prescriptionOverride,
          },
          lineItem,
          fulfillingPharmacyId,
          source,
          isReshipment,
          isMarkedForFillLater,
          isManualReview,
        };
      }
    ),
  };
};

export const transformGetPrescriptionData = (getPrescriptionDataResponse) => {
  const {
    prescription,
    interactionData,
    orderId,
    lineItemId,
    prescriptionId,
    parentLineItemQuantity,
  } = getPrescriptionDataResponse;

  const existingPrescriptions = interactionData?.existingPrescriptions || [];
  const status = interactionData?.status;

  /**
   * Why the order is in Direction Review, and the sig the direction already resolves to when there
   * is one. All three are absent on reviews raised before the service began recording them, so the
   * screen treats a missing reason as "no reason known" rather than a default.
   *
   * currentSig is a dispensing instruction and is passed through verbatim: recasing it would
   * change clinical text.
   */
  const directionReview = {
    reviewReason: interactionData?.reviewReason,
    currentSigId: interactionData?.currentSigId,
    currentSig: interactionData?.currentSig,
  };

  const prescriptionData = constructDataForDuplicationReview([
    { ...prescription, parentLineItemQuantity },
  ])[0];

  const transformedExistingPrescriptions = constructDataForDuplicationReview(existingPrescriptions);

  return {
    prescriptionData,
    existingPrescriptions: transformedExistingPrescriptions,
    status,
    directionReview,
    orderId,
    lineItemId,
    prescriptionId,
    parentLineItemQuantity,
  };
};

export const transformListOverrideDetails = ({
  listPrescriptionOverrideAudits: overrideDetails,
}) => {
  const formatObject = (obj) => ({
    editedTimestamp: obj?.editedTimestamp,
    fieldName: obj?.editedDetails?.fieldName,
    user: !isEmpty(obj) && `${obj?.user?.firstName} ${obj?.user?.lastName}`,
  });

  const lastOverrides = mapValues(overrideDetails, (value, key) => {
    if (key === 'prescriptionDetails') {
      return mapValues(value, (val) => (!isEmpty(val) ? formatObject(last(val)) : {}));
    }

    return !isEmpty(value) ? formatObject(last(value)) : {};
  });

  return omit(merge({}, lastOverrides, lastOverrides.prescriptionDetails), 'prescriptionDetails');
};
